// [ERP-FLOATING-CHAT-MEMO-POSITION-PER-DEVICE-DEV-20260629-001]
// 플로팅 채팅/메모 위젯 위치를 기기/브라우저별로 저장(localStorage). DB 저장 없음.
// 같은 직원이라도 포장대 PC / 사무실 PC / 아이패드에서 원하는 위치가 다르므로
// 계정 DB 가 아니라 기기 단위 localStorage 에 저장한다.

import type { CSSProperties } from "react";

export type FloatingWidget = "chat" | "memo";

export type FloatingPosition =
  | "bottom-right"
  | "middle-right"
  | "top-right"
  | "bottom-left"
  | "middle-left"
  | "top-left";

export const FLOATING_POSITION_OPTIONS: { value: FloatingPosition; label: string }[] = [
  { value: "bottom-right", label: "오른쪽 아래" },
  { value: "middle-right", label: "오른쪽 중간" },
  { value: "top-right",    label: "오른쪽 위" },
  { value: "bottom-left",  label: "왼쪽 아래" },
  { value: "middle-left",  label: "왼쪽 중간" },
  { value: "top-left",     label: "왼쪽 위" },
];

// 기본 위치 — 기존과 동일하게 둘 다 오른쪽 아래. 단 chat/memo 의 bottom offset 은
// 서로 겹치지 않게 다르게 둔다(chat=아래쪽 24px, memo=그 위 96px).
const DEFAULT_POSITION: FloatingPosition = "bottom-right";

const STORAGE_KEY: Record<FloatingWidget, string> = {
  chat: "erp.floating.chat.position",
  memo: "erp.floating.memo.position",
};

// [ERP-FLOATING-CHAT-MEMO-VISIBILITY-PER-DEVICE-DEV-20260629-001]
// 위젯 표시/숨김(기기별 localStorage). 기본 둘 다 표시.
const VISIBLE_KEY: Record<FloatingWidget, string> = {
  chat: "erp.floating.chat.visible",
  memo: "erp.floating.memo.visible",
};

// 설정(위치/표시) 변경을 같은 탭의 다른 컴포넌트(위젯 ↔ 전역 설정 버튼)에 알리는 이벤트.
// storage 이벤트는 "다른 탭"에서만 발생하므로, 같은 탭 동기화용 커스텀 이벤트를 별도로 둔다.
export const FLOATING_SETTINGS_EVENT = "erp:floating-settings-changed";

function emitFloatingSettingsChanged(): void {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new Event(FLOATING_SETTINGS_EVENT));
  } catch {
    /* ignore */
  }
}

/** 위젯 표시 여부 읽기. 'false' 저장 시에만 숨김, 그 외(없음/기타)는 기본 표시. */
export function loadFloatingVisible(widget: FloatingWidget): boolean {
  if (typeof window === "undefined") return true;
  try {
    return window.localStorage.getItem(VISIBLE_KEY[widget]) !== "false";
  } catch {
    return true;
  }
}

/** 위젯 표시 여부 저장 + 같은 탭 동기화 이벤트 emit. */
export function saveFloatingVisible(widget: FloatingWidget, visible: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(VISIBLE_KEY[widget], visible ? "true" : "false");
  } catch {
    /* ignore */
  }
  emitFloatingSettingsChanged();
}

const VALID = new Set<FloatingPosition>([
  "bottom-right", "middle-right", "top-right",
  "bottom-left", "middle-left", "top-left",
]);

/** localStorage 에서 위젯 위치 읽기. 없거나 잘못되면 기본값(bottom-right). */
export function loadFloatingPosition(widget: FloatingWidget): FloatingPosition {
  if (typeof window === "undefined") return DEFAULT_POSITION;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY[widget]);
    if (raw && VALID.has(raw as FloatingPosition)) return raw as FloatingPosition;
  } catch {
    /* ignore */
  }
  return DEFAULT_POSITION;
}

/** 위젯 위치 저장. */
export function saveFloatingPosition(widget: FloatingWidget, pos: FloatingPosition): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY[widget], pos);
  } catch {
    /* ignore */
  }
  emitFloatingSettingsChanged();
}

/** 위젯 위치 초기화(기본값으로). */
export function resetFloatingPosition(widget: FloatingWidget): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY[widget]);
  } catch {
    /* ignore */
  }
  emitFloatingSettingsChanged();
}

// [ERP-FLOATING-CHAT-MEMO-VISIBILITY-PER-DEVICE-DEV-20260629-001]
/** 채팅/메모 위치+표시 전체 기본값(visible=true, position=bottom-right) 복구. */
export function resetAllFloatingSettings(): void {
  if (typeof window === "undefined") return;
  try {
    for (const w of ["chat", "memo"] as FloatingWidget[]) {
      window.localStorage.removeItem(STORAGE_KEY[w]);
      window.localStorage.removeItem(VISIBLE_KEY[w]);
    }
  } catch {
    /* ignore */
  }
  emitFloatingSettingsChanged();
}

// 위젯별 같은-축 offset(px). 채팅/메모가 같은 프리셋이어도 서로 겹치지 않도록
// 짧은 변(아래/위) 기준 offset 을 다르게 준다. chat 이 바깥(24), memo 가 안쪽(96).
const EDGE_BASE = 24;   // 기본 가장자리 여백
const EDGE_STACK = 96;  // 두 번째 위젯 stack 여백(헤더/사이드바 회피 겸용)
const SIDE_MARGIN = 24; // 좌/우 가장자리 여백

/**
 * 프리셋 + 위젯 → inline style.
 *   - 세로: bottom/top + (chat=바깥, memo=stack) offset 으로 겹침 방지.
 *   - 가로: right/left 24px.
 *   - middle: top 50% + translateY(-50%). 단 chat/memo 동시 middle 이면 살짝 분리.
 *   - top: 헤더/사이드바 회피 위해 96px 이상.
 * position:fixed 는 호출측 className 에서 유지. 여기선 top/bottom/left/right/transform 만 반환.
 */
export function floatingPositionStyle(
  widget: FloatingWidget,
  pos: FloatingPosition,
): CSSProperties {
  const edge = widget === "chat" ? EDGE_BASE : EDGE_STACK;
  const horizontal: CSSProperties =
    pos.endsWith("-right") ? { right: SIDE_MARGIN } : { left: SIDE_MARGIN };

  if (pos.startsWith("bottom-")) {
    return { ...horizontal, bottom: edge, top: "auto" };
  }
  if (pos.startsWith("top-")) {
    // 헤더/사이드바 회피(96px+). 두 위젯이 같은 top 이면 memo 를 살짝 아래로.
    const top = widget === "chat" ? 96 : 96 + 72;
    return { ...horizontal, top, bottom: "auto" };
  }
  // middle — 두 위젯 동시 middle 이면 chat 위 / memo 아래로 살짝 분리.
  const shift = widget === "chat" ? "-50%" : "calc(-50% + 72px)";
  return { ...horizontal, top: "50%", bottom: "auto", transform: `translateY(${shift})` };
}
